// =============================================
// SocialProfilesEditor — reusable dla author (Person.sameAs) + organization
// (Organization.sameAs). Per-platform local state + signature-based init.
//
// 2026-05-17 noc (Brief B): naprawiony cross-field bug Facebook → Blogger.
// Stara wersja używała `urlTemplate.split("/{slug}")[0]` co dla template
// `https://{slug}.blogspot.com` zwracało `"https:/"` (1 slash) i matchowało
// KAŻDĄ URL → Blogger toggle automatycznie się włączał z URL pierwszej platformy.
// =============================================

import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Twitter,
  Linkedin,
  Link2,
  Facebook,
  BookOpen,
  Instagram,
  Users,
  FileText,
  Youtube,
  AlertTriangle,
  Wand2,
} from "lucide-react";
import type { SocialPlatform } from "@/services/schema/socialPlatforms";
import { fillTemplate } from "@/services/schema/socialPlatforms";

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  Twitter,
  Linkedin,
  Link2,
  Facebook,
  BookOpen,
  Instagram,
  Users,
  FileText,
  Youtube,
};

interface SocialProfilesEditorProps {
  platforms: SocialPlatform[];
  slug: string;
  value: string[];
  onChange: (urls: string[]) => void;
  scopeLabel?: string;
}

/**
 * Sygnatura domeny per platforma — UNIQUE per platform, handluje też
 * subdomain pattern (Blogger `{slug}.blogspot.com`).
 *   `https://twitter.com/{slug}`        → `twitter.com`
 *   `https://linkedin.com/in/{slug}`    → `linkedin.com/in`
 *   `https://medium.com/@{slug}`        → `medium.com/@`
 *   `https://{slug}.blogspot.com`       → `.blogspot.com`
 */
function getDomainSignature(template: string): string {
  return template
    .replace(/^https?:\/\//, "")
    .replace(/\{slug\}/, "")
    .replace(/\/$/, "");
}

interface PlatformRow {
  url: string;
  included: boolean;
}

function initRows(
  platforms: SocialPlatform[],
  slug: string,
  value: string[],
): Map<string, PlatformRow> {
  const map = new Map<string, PlatformRow>();
  for (const p of platforms) {
    const sig = getDomainSignature(p.urlTemplate);
    const defaultUrl = fillTemplate(p.urlTemplate, slug || "autor");
    // Strict match: URL musi zawierać signature (np. ".blogspot.com" dla Blogger).
    // Plus aliases (Twitter -> ["x.com"]) żeby rebrand domain matchował tę samą platformę.
    const signatures = [sig, ...(p.aliases ?? [])].filter((s) => s.length > 3);
    const matched = value.find(
      (u) => typeof u === "string" && signatures.some((s) => u.includes(s)),
    );
    map.set(p.id, {
      url: matched || defaultUrl,
      included: Boolean(matched),
    });
  }
  return map;
}

function computeValueFromRows(rows: Map<string, PlatformRow>): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const row of rows.values()) {
    if (!row.included) continue;
    const url = row.url.trim();
    if (!url) continue;
    if (!url.startsWith("https://")) continue;
    // Pre-save: pathname must be non-empty (no `https://facebook.com/` bare).
    try {
      const parsed = new URL(url);
      if (!parsed.pathname || parsed.pathname === "/") continue;
    } catch {
      continue;
    }
    const key = url.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(url);
  }
  return out;
}

function valuesEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const setA = new Set(a.map((u) => u.toLowerCase()));
  for (const u of b) if (!setA.has(u.toLowerCase())) return false;
  return true;
}

export function SocialProfilesEditor({
  platforms,
  slug,
  value,
  onChange,
  scopeLabel = "",
}: SocialProfilesEditorProps) {
  const [rows, setRows] = useState<Map<string, PlatformRow>>(() => initRows(platforms, slug, value));
  // Track ostatni emitowany value żeby uniknąć re-init z naszych własnych zmian.
  const lastEmitted = useRef<string[]>(computeValueFromRows(rows));

  // Re-sync z parent gdy value pochodzi z ZEWNĄTRZ (np. profile reload, Wyczyść).
  // Skip gdy value === lastEmitted (znaczy że to my właśnie wysłaliśmy).
  useEffect(() => {
    if (valuesEqual(value, lastEmitted.current)) return;
    setRows(initRows(platforms, slug, value));
    lastEmitted.current = value;
  }, [value, platforms, slug]);

  const commit = (next: Map<string, PlatformRow>) => {
    const newValue = computeValueFromRows(next);
    lastEmitted.current = newValue;
    setRows(next);
    onChange(newValue);
  };

  const toggleInclude = (platformId: string, included: boolean) => {
    const next = new Map(rows);
    const row = next.get(platformId);
    if (!row) return;
    next.set(platformId, { ...row, included });
    commit(next);
  };

  const onUrlChange = (platformId: string, newUrl: string) => {
    const next = new Map(rows);
    const row = next.get(platformId);
    if (!row) return;
    next.set(platformId, { ...row, url: newUrl });
    commit(next);
  };

  /**
   * Auto-wypełnij — fill URL TYLKO dla platforms gdzie toggle === true.
   * NIE włącza dodatkowych toggle (zmiana semantyki vs poprzednia wersja
   * która włączała wszystkie 8).
   */
  const autoFillIncluded = () => {
    const next = new Map(rows);
    let touchedAny = false;
    for (const p of platforms) {
      const row = next.get(p.id);
      if (!row || !row.included) continue;
      next.set(p.id, { ...row, url: fillTemplate(p.urlTemplate, slug || "autor") });
      touchedAny = true;
    }
    if (touchedAny) commit(next);
  };

  const clearAll = () => {
    const next = new Map(rows);
    for (const p of platforms) {
      const row = next.get(p.id);
      if (!row) continue;
      next.set(p.id, { ...row, included: false });
    }
    commit(next);
  };

  const includedCount = Array.from(rows.values()).filter((r) => r.included).length;

  return (
    <div className="space-y-2">
      <div className="rounded-md border border-border/50 bg-muted/20 divide-y divide-border/40">
        {platforms.map((platform) => {
          const Icon = ICON_MAP[platform.iconName] || Link2;
          const row = rows.get(platform.id) || {
            url: fillTemplate(platform.urlTemplate, slug || "autor"),
            included: false,
          };
          const defaultUrl = fillTemplate(platform.urlTemplate, slug || "autor");
          const looksLikeAutoFill = row.url.trim() === defaultUrl;
          return (
            <div key={platform.id} className="flex items-center gap-2 px-3 py-2">
              <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="w-36 shrink-0 text-xs font-medium truncate">
                {platform.displayName}
              </span>
              <Input
                value={row.url}
                onChange={(e) => onUrlChange(platform.id, e.target.value)}
                placeholder={platform.exampleUrl}
                className="text-xs h-7 flex-1"
              />
              {row.included && looksLikeAutoFill && (
                <span
                  className="text-[9px] text-amber-500 px-1.5 py-0.5 rounded bg-amber-500/10 border border-amber-500/30 shrink-0"
                  title="URL wygenerowany ze slug — zweryfikuj że profil naprawdę istnieje przed save."
                >
                  auto
                </span>
              )}
              <Switch
                checked={row.included}
                onCheckedChange={(v) => toggleInclude(platform.id, v)}
                aria-label={`Include ${platform.displayName} in schema sameAs`}
              />
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={autoFillIncluded}
          disabled={!slug || includedCount === 0}
          className="h-7 text-xs"
          title="Wypełnia URL ze slug TYLKO dla włączonych toggle. NIE włącza dodatkowych."
        >
          <Wand2 className="h-3 w-3 mr-1.5" />
          Auto-wypełnij włączone ze slug
        </Button>
        {includedCount > 0 && (
          <Button
            size="sm"
            variant="ghost"
            onClick={clearAll}
            className="h-7 text-xs text-muted-foreground"
          >
            Wyłącz wszystkie
          </Button>
        )}
        <span className="text-[10px] text-muted-foreground ml-auto">
          {includedCount} / {platforms.length} włączone
        </span>
      </div>

      <Alert className="border-amber-500/30 bg-amber-500/5">
        <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
        <AlertDescription className="text-[11px] text-amber-700 dark:text-amber-300">
          URLe {scopeLabel || "w sameAs"} MUSZĄ prowadzić do istniejących profili (nawet pustych).
          Broken sameAs = negatywny sygnał Trust Calibration SRO 1.2. Lepiej pusty niż fałszywy.
        </AlertDescription>
      </Alert>
    </div>
  );
}
